/** Behavioral interview prep — STAR, leadership principles, high-signal themes. */

export const behavioralContent = {
  'amazon-leadership-principles': {
    title: 'Amazon Leadership Principles',
    subtitle: 'Amazon evaluates every answer against sixteen leadership principles — know them before you walk in.',
    readingMinutes: 12,
    blocks: [
      {
        type: 'p',
        text: 'Amazon does not ask random “tell me about yourself” questions. Interviewers are trained to probe specific Leadership Principles (LPs). Your job is to show you already behave this way — with real examples, not slogans.',
      },
      {
        type: 'h',
        text: 'The sixteen principles (grouped for memory)',
      },
      {
        type: 'table',
        headers: ['Group', 'Principles'],
        rows: [
          ['Customer & results', 'Customer Obsession, Deliver Results, Ownership, Insist on the Highest Standards'],
          ['Thinking & decisions', 'Dive Deep, Invent and Simplify, Are Right, A Lot, Learn and Be Curious, Bias for Action'],
          ['People & culture', 'Hire and Develop the Best, Strive to Be Earth\'s Best Employer, Earn Trust, Have Backbone; Disagree and Commit'],
          ['Scale & frugality', 'Think Big, Frugality, Deliver Results'],
        ],
      },
      {
        type: 'callout',
        tone: 'interview',
        title: 'How Amazon scores you',
        text: 'Interviewers take structured notes mapped to LPs. A vague story scores “no data.” A specific story with your role, trade-offs, and measurable outcome scores strong. They often ask “What would you do differently?” — have an honest answer ready.',
      },
      {
        type: 'h',
        text: 'How to prepare',
      },
      {
        type: 'ol',
        items: [
          'Read the official LP page once; don\'t memorize word-for-word — internalize behaviors.',
          'Build 2–3 STAR stories per high-frequency LP (Ownership, Customer Obsession, Dive Deep, Bias for Action, Deliver Results, Invent and Simplify).',
          'Tag each story with 2–3 LPs it demonstrates — stories should be reusable.',
          'Practice aloud with a timer: 2 minutes per story, then 1 minute of follow-ups.',
        ],
      },
      {
        type: 'h',
        text: 'Sample question → LP mapping',
      },
      {
        type: 'table',
        headers: ['Question flavor', 'Likely LP'],
        rows: [
          ['Tell me about a time you went above and beyond for a customer', 'Customer Obsession'],
          ['Describe a project you owned end-to-end', 'Ownership'],
          ['When did you disagree with your manager?', 'Have Backbone; Disagree and Commit'],
          ['Tell me about a mistake and what you learned', 'Learn and Be Curious'],
          ['How did you reduce cost or simplify a system?', 'Invent and Simplify, Frugality'],
        ],
      },
      {
        type: 'callout',
        tone: 'tip',
        title: 'Answer structure that works',
        text: 'Lead with the LP behavior in one sentence (“I’ll share an example of diving deep into a production issue…”), then STAR. End with impact in numbers when possible and one sentence on what you learned.',
      },
    ],
    references: [
      { label: 'Amazon Leadership Principles', url: 'https://www.amazon.jobs/content/en/our-workplace/leadership-principles' },
      { label: 'Amazon Interview Process', url: 'https://www.amazon.jobs/en/landing_pages/interview-process' },
    ],
  },

  'googles-behavioral-interview-guidance': {
    title: "Google's Behavioral Interview Guidance",
    subtitle: 'Google looks for general cognitive ability, leadership, role-related knowledge, and Googleyness — behavioral rounds test the first two deeply.',
    readingMinutes: 10,
    blocks: [
      {
        type: 'p',
        text: 'Google’s loop includes behavioral interviews (often called “Googleyness & Leadership” or G&L). They want evidence that you collaborate well, handle ambiguity, and make decisions with data and humility — not that you’ve memorized a script.',
      },
      {
        type: 'h',
        text: 'What Google evaluates',
      },
      {
        type: 'ul',
        items: [
          'General cognitive ability — structured thinking, learning speed, handling incomplete information.',
          'Leadership — influence without authority, mentoring, stepping up when needed.',
          'Googleyness — comfort with ambiguity, teamwork, putting users first, ethical judgment.',
          'Role-related knowledge — shown more in technical rounds, but behavioral stories should match your level.',
        ],
      },
      {
        type: 'callout',
        tone: 'interview',
        title: 'Google follow-up style',
        text: 'Interviewers dig with “Tell me more about…” and “What did you specifically do?” They reject stories where “we” did everything and “I” is unclear. Be ready to name your exact contribution.',
      },
      {
        type: 'h',
        text: 'Common question themes',
      },
      {
        type: 'ul',
        items: [
          'Conflict on a team — how you resolved it without burning relationships.',
          'Failure — what happened, your role, what you changed afterward.',
          'Ambiguous project — how you defined success and aligned stakeholders.',
          'Helping others succeed — mentoring, unblocking, code review culture.',
          'Pushing back on a bad idea — data, alternatives, outcome.',
        ],
      },
      {
        type: 'table',
        headers: ['Weak signal', 'Strong signal'],
        rows: [
          ['“I’m a hard worker and team player”', 'Concrete story with timeline, stakeholders, and outcome'],
          ['Blaming others for failure', 'Owning your part + systemic fix'],
          ['Only individual heroics', 'Collaboration + when you asked for help'],
          ['No metrics', 'Latency cut 40%, incidents down 3→0/month, NPS +12'],
        ],
      },
      {
        type: 'callout',
        tone: 'tip',
        text: 'Google interviewers often prefer slightly imperfect but honest stories over polished fiction. If the outcome was mixed, say what you learned — that can still pass if your reasoning was sound.',
      },
    ],
    references: [
      { label: 'Google Careers — How We Hire', url: 'https://careers.google.com/how-we-hire/' },
      { label: 'Google Interview Tips (Official)', url: 'https://careers.google.com/how-we-hire/interview/' },
    ],
  },

  'star-method': {
    title: 'STAR Method',
    subtitle: 'Situation, Task, Action, Result — the simplest framework that keeps behavioral answers focused and interview-ready.',
    readingMinutes: 8,
    blocks: [
      {
        type: 'p',
        text: 'STAR is not a template to recite robotically. It is a pacing tool: interviewers get context fast, see your decisions, and hear impact. Most strong answers land in 90 seconds to 2 minutes.',
      },
      {
        type: 'steps',
        items: [
          { title: 'Situation (15–20 sec)', text: 'One or two sentences: team, product, scale, why it mattered. Skip company history.' },
          { title: 'Task (10–15 sec)', text: 'Your responsibility — not the whole team’s. “I was the backend owner for checkout reliability.”' },
          { title: 'Action (60–90 sec)', text: 'What you did, in order. Include trade-offs, who you influenced, and one obstacle you overcame.' },
          { title: 'Result (15–20 sec)', text: 'Outcome with numbers if possible, plus one learning or process change that stuck.' },
        ],
      },
      {
        type: 'h',
        text: 'STAR example (production incident)',
      },
      {
        type: 'table',
        headers: ['STAR', 'Example content'],
        rows: [
          ['Situation', 'Payment API p99 latency spiked to 8s during a sale; ~2% of checkouts failed.'],
          ['Task', 'I owned the payment service and was on-call for the incident bridge.'],
          ['Action', 'Correlated traces with DB slow-query logs, found missing index after a migration; rolled forward a hotfix index, added a query timeout and dashboard alert.'],
          ['Result', 'p99 back to 400ms within 2 hours; zero failed checkouts post-fix; runbook updated — no repeat in 6 months.'],
        ],
      },
      {
        type: 'callout',
        tone: 'warn',
        title: 'Common STAR mistakes',
        text: 'Spending 70% of time on Situation. Listing team actions without “I.” Ending with no measurable Result. Using the same story for every question — interviewers notice.',
      },
      {
        type: 'callout',
        tone: 'tip',
        title: 'Interview tip',
        text: 'Write Actions as bullet verbs: investigated, proposed, implemented, aligned, measured. This keeps you from drifting into passive voice.',
      },
      {
        type: 'h',
        text: 'Timing drill',
      },
      {
        type: 'p',
        text: 'Record a 2-minute STAR answer. If Situation exceeds 30 seconds, cut. Interviewers interrupt long context — front-load your role and impact.',
      },
      {
        type: 'callout',
        tone: 'interview',
        text: 'Amazon bar raiser may ask three follow-ups on one story — prepare depth on Action decisions, not only Result.',
      },
    ],
    references: [
      { label: 'STAR Method (MIT Career Advising)', url: 'https://capd.mit.edu/resources/the-star-method-for-behavioral-interviews/' },
      { label: 'Amazon LP interview prep (uses STAR)', url: 'https://www.amazon.jobs/content/en/our-workplace/leadership-principles' },
    ],
  },

  'building-your-story-bank-25': {
    title: 'Building Your Story Bank (~25)',
    subtitle: 'A small set of well-prepared stories beats twenty vague memories — aim for about 25 that map to multiple themes.',
    readingMinutes: 11,
    blocks: [
      {
        type: 'p',
        text: 'Senior candidates rarely improvise under pressure. They reuse a curated story bank — each story tagged with themes (ownership, conflict, failure, leadership) and leadership principles. You need enough variety that you never repeat the same example twice in one loop.',
      },
      {
        type: 'h',
        text: 'Suggested bank structure (~25 stories)',
      },
      {
        type: 'table',
        headers: ['Category', 'Count', 'Examples'],
        rows: [
          ['Ownership & delivery', '5', 'Led feature zero to prod, rescued slipped project, on-call fix, cross-team dependency unblocked'],
          ['Failure & learning', '3', 'Bad deploy, wrong technical bet, missed deadline — each with real learning'],
          ['Conflict & influence', '4', 'Disagreement with PM, peer conflict, pushing back on architecture, stakeholder alignment'],
          ['Leadership & mentoring', '4', 'Mentored intern/junior, led guild, drove code quality initiative, hiring loop participation'],
          ['Customer / user impact', '3', 'User-reported bug swarm, accessibility win, metric-driven UX improvement'],
          ['Innovation & simplification', '3', 'Automated toil, deleted legacy system, clever optimization with trade-offs'],
          ['Scale & incidents', '3', 'Performance at scale, outage leadership, capacity planning win'],
        ],
      },
      {
        type: 'h',
        text: 'Story card template',
      },
      {
        type: 'code',
        language: 'text',
        title: 'One index card (or spreadsheet row) per story',
        code: `Title: Checkout latency incident (Q3 2024)
Themes: Dive Deep, Ownership, Deliver Results
LPs: Customer Obsession, Ownership
STAR one-liner: Sale spike → missing index → hotfix + alerts → p99 8s→400ms
Follow-ups ready: What would you do differently? How did you communicate to PM?
Metrics: p99 latency, error rate, time to mitigate`,
      },
      {
        type: 'ol',
        items: [
          'Mine your last 3–5 years: projects, incidents, conflicts, promotions, mentoring.',
          'Write bullet STAR outlines — not essays — one page total for all 25.',
          'Record yourself telling each story in under 2 minutes; cut fluff.',
          'Mark stories “overused” after each mock so you rotate in loops.',
        ],
      },
      {
        type: 'callout',
        tone: 'interview',
        title: 'What interviewers notice',
        text: 'Using one heroic story for every question signals shallow experience. Using different stories with consistent values signals maturity.',
      },
      {
        type: 'callout',
        tone: 'tip',
        text: 'Spreadsheet columns: Title | STAR one-liner | LPs | Themes | Last used date — refresh before each interview loop.',
      },
    ],
    references: [
      { label: 'Behavioral interview prep (Harvard FAS)', url: 'https://careerservices.fas.harvard.edu/resources/take-action/interviewing/' },
      { label: 'Amazon Leadership Principles', url: 'https://www.amazon.jobs/content/en/our-workplace/leadership-principles' },
    ],
  },

  ownership: {
    title: 'Ownership',
    subtitle: 'Show you treat the problem like yours long after the ticket is closed — not just your assigned slice.',
    readingMinutes: 9,
    blocks: [
      {
        type: 'p',
        text: 'Ownership questions test whether you wait for permission or drive outcomes. Strong answers show end-to-end responsibility: discovery, execution, communication, and follow-through (monitoring, docs, handoff).',
      },
      {
        type: 'h',
        text: 'Sample prompts',
      },
      {
        type: 'ul',
        items: [
          'Tell me about something you owned outside your official job description.',
          'Describe a time you saw a problem no one else was fixing.',
          'When did you take accountability for a failure?',
        ],
      },
      {
        type: 'table',
        headers: ['STAR outline', 'Backend example'],
        rows: [
          ['Situation', 'Logging was inconsistent across 12 microservices; on-call spent hours grepping logs during incidents.'],
          ['Task', 'Not assigned — I proposed standardizing structured logging as a platform initiative.'],
          ['Action', 'Drafted RFC, built reference library, migrated two critical services, paired with teams, added CI lint for log schema.'],
          ['Result', 'MTTR dropped ~35%; 8/12 services migrated in one quarter; pattern adopted by platform team.'],
        ],
      },
      {
        type: 'callout',
        tone: 'tip',
        title: 'Signals to include',
        text: 'You identified the gap. You did not say “not my job.” You left things better for the next person (runbooks, alerts, ownership docs).',
      },
      {
        type: 'callout',
        tone: 'interview',
        title: 'Follow-ups',
        text: '“What pushback did you get?” “How did you prioritize against feature work?” Have answers — they test whether ownership was reckless or disciplined.',
      },
      {
        type: 'h',
        text: 'Anti-patterns',
      },
      {
        type: 'ul',
        items: [
          'Hero narrative with no team context.',
          'Fixing only your team\'s symptom while root cause hurts org.',
          'Abandoning problem after first PR merged — no metrics or handoff.',
        ],
      },
    ],
    references: [
      { label: 'Amazon LP — Ownership', url: 'https://www.amazon.jobs/content/en/our-workplace/leadership-principles' },
    ],
  },

  'failure-and-learning': {
    title: 'Failure & Learning',
    subtitle: 'Interviewers want honest failures with clear learning — not fake “humble brags.”',
    readingMinutes: 8,
    blocks: [
      {
        type: 'p',
        text: 'Pick a real failure where you had agency. The story should show vulnerability, analysis, and changed behavior — not “the only failure was working too hard.”',
      },
      {
        type: 'h',
        text: 'Sample story outline',
      },
      {
        type: 'table',
        headers: ['STAR', 'Example'],
        rows: [
          ['Situation', 'Led migration to a new cache layer under a tight deadline.'],
          ['Task', 'I owned the rollout plan and go/no-go call.'],
          ['Action', 'Skipped gradual rollout; cache stampede caused 20 min partial outage. Led rollback, postmortem, added canary + load test gate.'],
          ['Result', 'Restored service; next three migrations zero customer impact; team adopted mandatory rollout checklist.'],
        ],
      },
      {
        type: 'ul',
        items: [
          'Name what you would do differently — specifically, not vaguely.',
          'Show learning that stuck (process, test, metric), not one-time apology.',
          'Avoid failures that make you look careless (data loss without safeguards) unless you fixed the system deeply.',
        ],
      },
      {
        type: 'callout',
        tone: 'warn',
        text: 'Do not blame the team for your decision. Interviewers listen for “I underestimated…” vs “They didn’t listen to me.”',
      },
      {
        type: 'h',
        text: 'Sample prompts',
      },
      {
        type: 'ul',
        items: [
          'Tell me about a time you failed.',
          'Describe a project that did not go as planned.',
          'What is your biggest professional mistake?',
        ],
      },
      {
        type: 'callout',
        tone: 'tip',
        title: 'Learning sentence template',
        text: '“Since then, I always [specific behavior change] — for example [brief proof it stuck].”',
      },
    ],
    references: [
      { label: 'Amazon LP — Learn and Be Curious', url: 'https://www.amazon.jobs/content/en/our-workplace/leadership-principles' },
    ],
  },

  conflict: {
    title: 'Conflict',
    subtitle: 'Demonstrate you disagree with respect, use data, and optimize for the best outcome — not winning the argument.',
    readingMinutes: 9,
    blocks: [
      {
        type: 'p',
        text: 'Conflict questions are not about drama. They assess emotional intelligence, influence, and whether teammates would want to work with you again.',
      },
      {
        type: 'h',
        text: 'Framework for a strong answer',
      },
      {
        type: 'steps',
        items: [
          { title: 'Stake the disagreement clearly', text: 'What two options existed? What was at risk (latency, cost, ship date)?' },
          { title: 'Show you listened', text: 'Repeat the other person’s concern fairly before your counter.' },
          { title: 'Use evidence', text: 'Prototype, benchmark, user data, incident history — not seniority.' },
          { title: 'Close the loop', text: 'Decision made, relationship intact, outcome measured.' },
        ],
      },
      {
        type: 'table',
        headers: ['STAR sketch', 'Tech lead vs PM on scope'],
        rows: [
          ['Situation', 'PM wanted full rewrite; eng had 6 weeks before peak season.'],
          ['Task', 'I needed to protect reliability without blocking all new features.'],
          ['Action', 'Proposed phased plan with risk matrix; ran spike proving 80% value from incremental refactor; weekly sync with shared doc.'],
          ['Result', 'Shipped incremental path; zero Sev-1 in peak; PM later cited approach in planning template.'],
        ],
      },
      {
        type: 'callout',
        tone: 'tip',
        text: 'Never describe the other person as “incompetent” or “clueless.” Interviewers assume you’ll say the same about them later.',
      },
      {
        type: 'h',
        text: 'Sample prompts',
      },
      {
        type: 'ul',
        items: [
          'Tell me about a disagreement with a coworker.',
          'How did you handle a difficult stakeholder?',
          'Describe a time you had to influence without authority.',
        ],
      },
      {
        type: 'callout',
        tone: 'interview',
        text: 'Google maps conflict to collaboration — outcome should show relationship still works post-decision.',
      },
    ],
    references: [
      { label: 'Amazon LP — Have Backbone; Disagree and Commit', url: 'https://www.amazon.jobs/content/en/our-workplace/leadership-principles' },
    ],
  },

  'promotion-and-growth': {
    title: 'Promotion & Growth',
    subtitle: 'Talk about scope expansion, sustained impact, and how you earned trust — not just tenure.',
    readingMinutes: 8,
    blocks: [
      {
        type: 'p',
        text: 'Promotion stories should show you were already operating at the next level for a meaningful period: broader ownership, higher ambiguity, developing others, and business impact.',
      },
      {
        type: 'ul',
        items: [
          'Before: scope, level, constraints.',
          'Growth actions: skills built, initiatives led, feedback you acted on.',
          'Evidence: promo packet themes — impact, scope, influence, consistency.',
          'Humility: mentors, luck, team — without hiding your contribution.',
        ],
      },
      {
        type: 'table',
        headers: ['Prompt', 'What to emphasize'],
        rows: [
          ['Why were you promoted?', 'Sustained impact at next-level scope, not one lucky project'],
          ['Tell me about a time you grew someone else', 'Mentoring tied to their measurable growth'],
          ['Career pivot story', 'Deliberate skill building + proof projects'],
        ],
      },
      {
        type: 'callout',
        tone: 'interview',
        text: 'If you have not been promoted yet, tell a “scope growth” story: when you took on staff-level work as a senior — that still answers growth prompts.',
      },
      {
        type: 'h',
        text: 'STAR example — scope before title',
      },
      {
        type: 'table',
        headers: ['STAR', 'Example'],
        rows: [
          ['Situation', 'Team owned payments; no formal tech lead role.'],
          ['Task', 'I drove cross-service PCI compliance project affecting 4 teams.'],
          ['Action', 'Wrote RFC, ran weekly sync, unblocked teams on tokenization SDK, reported status to director.'],
          ['Result', 'Passed audit on first attempt; role officially recognized as tech lead next cycle.'],
        ],
      },
      {
        type: 'callout',
        tone: 'tip',
        title: 'Document growth',
        text: 'Keep a brag doc: projects, metrics, feedback quotes, scope expansions — promo packets and interviews both pull from it.',
      },
      {
        type: 'ul',
        items: [
          'Show feedback you received and how you applied it.',
          'Mention mentoring others — promotion often requires multiplying impact.',
          'Connect growth to business outcomes, not just courses completed.',
        ],
      },
    ],
    references: [
      { label: 'Staff Engineer book — career narratives', url: 'https://staffeng.com/book' },
    ],
  },

  leadership: {
    title: 'Leadership',
    subtitle: 'Leadership is influence and accountability — you do not need a manager title to demonstrate it.',
    readingMinutes: 9,
    blocks: [
      {
        type: 'p',
        text: 'Use stories where you set direction, unblocked others, or improved how the team works — without formal authority.',
      },
      {
        type: 'h',
        text: 'Strong leadership story ingredients',
      },
      {
        type: 'ul',
        items: [
          'Clear goal that mattered to users or the business.',
          'People aligned — engineers, PM, design, ops.',
          'You made hard calls or removed ambiguity.',
          'Lasting change: process, architecture, culture — not a one-off hero fix.',
        ],
      },
      {
        type: 'table',
        headers: ['STAR', 'Leading an incident response process'],
        rows: [
          ['Situation', 'Repeated chaotic incident bridges; no clear roles.'],
          ['Task', 'Volunteered to design incident response for our org.'],
          ['Action', 'Interviewed on-call engineers, adapted incident command roles, ran game days, trained leads.'],
          ['Result', 'MTTR down 40%; survey showed higher confidence on-call; adopted by sibling team.'],
        ],
      },
      {
        type: 'callout',
        tone: 'tip',
        text: 'Balance “I led” with “we delivered.” Credit the team; own your specific leadership moves.',
      },
      {
        type: 'h',
        text: 'Sample prompts',
      },
      {
        type: 'ul',
        items: [
          'Tell me about a time you led a project without being the manager.',
          'How have you improved team productivity or culture?',
          'Describe leading through ambiguity.',
        ],
      },
      {
        type: 'callout',
        tone: 'interview',
        text: 'Staff+ loops expect multi-team leadership — scope beyond your immediate squad when possible.',
      },
    ],
    references: [
      { label: 'Google Careers — Leadership in interviews', url: 'https://careers.google.com/how-we-hire/interview/' },
    ],
  },

  mentoring: {
    title: 'Mentoring',
    subtitle: 'Show you invest in others\' growth with structure, patience, and measurable outcomes.',
    readingMinutes: 7,
    blocks: [
      {
        type: 'p',
        text: 'Mentoring answers should name the mentee’s starting point, your approach, and their outcome — promotion, ownership of a service, faster ramp, improved code quality.',
      },
      {
        type: 'table',
        headers: ['Element', 'Example'],
        rows: [
          ['Mentee context', 'New grad on payments team, first production service'],
          ['Your approach', 'Weekly 1:1s, paired on first RFC, shadowed on-call, gradual ownership of one API'],
          ['Obstacle', 'Confidence gap — afraid to ask questions in big meetings'],
          ['Outcome', 'Shipped auth microservice solo in Q2; mentee now mentors intern'],
        ],
      },
      {
        type: 'callout',
        tone: 'interview',
        title: 'Avoid',
        text: 'Stories where you “fixed” them like a bug. Focus on empowerment and growing autonomy.',
      },
      {
        type: 'h',
        text: 'Sample prompts',
      },
      {
        type: 'ul',
        items: [
          'Tell me about someone you mentored and where they are now.',
          'How do you onboard a new engineer on your team?',
          'Describe feedback you gave that was hard to deliver.',
        ],
      },
      {
        type: 'steps',
        items: [
          { title: 'Assess', text: 'Identify skill gap and learning style.' },
          { title: 'Plan', text: 'Pair goals with incremental ownership milestones.' },
          { title: 'Support', text: 'Regular 1:1s, code review as teaching, safe space for questions.' },
          { title: 'Measure', text: 'Their shipped work, confidence, and eventually mentoring others.' },
        ],
      },
      {
        type: 'callout',
        tone: 'tip',
        text: 'Great mentoring stories end with the mentee succeeding independently — not depending on you forever.',
      },
      {
        type: 'callout',
        tone: 'interview',
        text: 'Amazon “Hire and Develop the Best” expects you to raise the bar — mentoring is evidence, not a nice extra.',
      },
    ],
    references: [
      { label: 'Amazon LP — Hire and Develop the Best', url: 'https://www.amazon.jobs/content/en/our-workplace/leadership-principles' },
    ],
  },

  'production-incident': {
    title: 'Production Incident',
    subtitle: 'Incident stories prove calm under pressure, technical depth, and improvement mindset.',
    readingMinutes: 10,
    blocks: [
      {
        type: 'p',
        text: 'Pick an incident where you had a real role — triage, root cause, communication, or prevention. Interviewers want the narrative arc: detect → mitigate → resolve → prevent recurrence.',
      },
      {
        type: 'steps',
        items: [
          { title: 'Detect', text: 'How did you know? Alerts, users, dashboard — and what was missing initially.' },
          { title: 'Mitigate', text: 'Stop the bleeding first (rollback, feature flag, scale up) — speed matters.' },
          { title: 'Diagnose', text: 'Hypothesis-driven debugging; tools (logs, traces, metrics).' },
          { title: 'Fix & prevent', text: 'Root cause fix + systemic guardrails (tests, limits, runbooks).' },
        ],
      },
      {
        type: 'table',
        headers: ['STAR', 'Database connection pool exhaustion'],
        rows: [
          ['Situation', 'API errors jumped 5×; checkout success rate dropped during lunch traffic.'],
          ['Task', 'Primary on-call for order service; incident commander for bridge.'],
          ['Action', 'Identified pool max too low after new deploy; temporary scale + pool config hotfix; added pool saturation alert and load test in CI.'],
          ['Result', 'Recovery in 28 minutes; no data corruption; similar class prevented in later deploys.'],
        ],
      },
      {
        type: 'callout',
        tone: 'warn',
        text: 'Do not skip customer impact or postmortem accountability. Hand-waving “we fixed it” fails Dive Deep questions.',
      },
      {
        type: 'h',
        text: 'Communication during incidents',
      },
      {
        type: 'ul',
        items: [
          'Status page updates every N minutes — even if “still investigating.”',
          'Internal Slack war room with clear roles: commander, scribe, comms.',
          'Blameless postmortem within 48 hours with action items and owners.',
        ],
      },
      {
        type: 'table',
        headers: ['Follow-up question', 'Strong answer angle'],
        rows: [
          ['What would you do differently?', 'Earlier canary, better alert threshold, runbook gap'],
          ['How did you prioritize?', 'Customer impact first, then root cause parallel'],
          ['Severity?', 'Quantify duration × affected users × revenue at risk'],
        ],
      },
      {
        type: 'callout',
        tone: 'interview',
        title: 'Map to LPs',
        text: 'Same incident story can show Ownership, Dive Deep, Customer Obsession, and Deliver Results — tag which LP you emphasize per question.',
      },
    ],
    references: [
      { label: 'Google SRE — Incident Management', url: 'https://sre.google/sre-book/managing-incidents/' },
    ],
  },

  'scaling-a-system-or-team': {
    title: 'Scaling a System or Team',
    subtitle: 'Whether traffic or headcount, scaling stories need before/after numbers and deliberate trade-offs.',
    readingMinutes: 9,
    blocks: [
      {
        type: 'p',
        text: 'This theme covers technical scale (10× traffic) and organizational scale (team doubled, you kept velocity). Clarify which type early in your answer.',
      },
      {
        type: 'h',
        text: 'System scaling example outline',
      },
      {
        type: 'table',
        headers: ['Phase', 'Content'],
        rows: [
          ['Before', 'Monolith handled 2k RPS; p95 900ms at peak'],
          ['Bottleneck', 'DB write hotspot on orders table'],
          ['Actions', 'Read replicas, cache aside for catalog, async checkout events, shard planning'],
          ['After', '15k RPS peak; p95 220ms; 99.9% availability during sale'],
        ],
      },
      {
        type: 'h',
        text: 'Team scaling example outline',
      },
      {
        type: 'ul',
        items: [
          'Hiring bar and onboarding docs shortened ramp from 12 to 6 weeks.',
          'Split services by bounded context to reduce merge conflicts.',
          'Introduced RFC process so eight teams could align without daily sync marathons.',
        ],
      },
      {
        type: 'callout',
        tone: 'tip',
        text: 'Mention what you intentionally did not do (premature microservices, over-hiring) — trade-off thinking impresses senior interviewers.',
      },
      {
        type: 'h',
        text: 'Sample prompts',
      },
      {
        type: 'ul',
        items: [
          'Tell me about scaling a system for growth.',
          'How did you grow an engineering team while maintaining quality?',
          'Describe handling 10× traffic increase.',
        ],
      },
    ],
    references: [
      { label: 'Amazon LP — Think Big', url: 'https://www.amazon.jobs/content/en/our-workplace/leadership-principles' },
    ],
  },

  'customer-obsession': {
    title: 'Customer Obsession',
    subtitle: 'Start from the customer problem and work backward — even when the fix is unglamorous backend work.',
    readingMinutes: 8,
    blocks: [
      {
        type: 'p',
        text: 'Customer obsession is not “the PM said users wanted it.” Show you sought customer pain (support tickets, session replay, interviews) and changed behavior or systems as a result.',
      },
      {
        type: 'table',
        headers: ['STAR', 'Reducing failed password resets'],
        rows: [
          ['Situation', 'Support volume spiked; 18% of reset emails bounced.'],
          ['Task', 'I owned auth email pipeline.'],
          ['Action', 'Traced deliverability + typo domains; added validation, retry with alternate provider, dashboard for bounce reasons.'],
          ['Result', 'Bounce rate 18%→2%; support tickets −30%; NPS on auth flow +8 points.'],
        ],
      },
      {
        type: 'callout',
        tone: 'interview',
        text: 'Amazon interviewers may ask: “How did you know customers cared?” Cite volume, revenue at risk, or verbatim feedback.',
      },
      {
        type: 'h',
        text: 'Working backward practice',
      },
      {
        type: 'ol',
        items: [
          'Start from customer pain in their words (support ticket, review, churn data).',
          'Define measurable success — not “better UX” but “reset completion +15%.”',
          'Challenge scope: smallest change that fixes root pain.',
          'Verify after ship with same metric channel.',
        ],
      },
      {
        type: 'callout',
        tone: 'tip',
        title: 'Internal customers count',
        text: 'Platform team serving other engineers — treat internal users as customers; NPS-style surveys and ticket volume are valid evidence.',
      },
      {
        type: 'callout',
        tone: 'interview',
        text: 'Weak: “We built what sales asked for.” Strong: “Support tickets showed 40% of churn calls mentioned onboarding — we fixed step 3.”',
      },
      {
        type: 'ul',
        items: [
          'Long-term trust beats short-term metric hacks.',
          'Say no to features that harm customer trust even if leadership pushes.',
          'Include edge cases: accessibility, international users, slow networks.',
        ],
      },
    ],
    references: [
      { label: 'Amazon LP — Customer Obsession', url: 'https://www.amazon.jobs/content/en/our-workplace/leadership-principles' },
    ],
  },

  innovation: {
    title: 'Innovation',
    subtitle: 'Innovation in interviews means a creative solution that shipped — with measured impact, not a slide deck.',
    readingMinutes: 8,
    blocks: [
      {
        type: 'p',
        text: 'Pair “Invent and Simplify” with a story where you replaced complexity with something maintainable, or found a 10× cheaper/faster approach.',
      },
      {
        type: 'ul',
        items: [
          'Old way cost: engineer hours, infra bill, latency, error rate.',
          'Your insight: what others missed.',
          'New way: simpler architecture, fewer moving parts when possible.',
          'Impact: metrics + whether others adopted it.',
        ],
      },
      {
        type: 'callout',
        tone: 'tip',
        title: 'Sample one-liner',
        text: '“We replaced a 14-step manual reconciliation with an idempotent nightly job — finance hours down 20h/week, errors near zero.”',
      },
      {
        type: 'h',
        text: 'STAR table — simplification win',
      },
      {
        type: 'table',
        headers: ['STAR', 'Example'],
        rows: [
          ['Situation', 'Three overlapping config systems caused misconfig outages.'],
          ['Task', 'I proposed consolidating to one source of truth.'],
          ['Action', 'Mapped all consumers, built migration tool, deprecated old APIs with sunset dates.'],
          ['Result', 'Config incidents 6/quarter → 0; onboarding time for new services −3 days.'],
        ],
      },
      {
        type: 'callout',
        tone: 'interview',
        text: 'Innovation is not only ML — deleting code, automating toil, and simplifying architecture are high-signal stories.',
      },
      {
        type: 'callout',
        tone: 'tip',
        text: 'Pair with Frugality LP at Amazon: innovation that saves money scores twice.',
      },
      {
        type: 'ul',
        items: [
          'Quantify before/after complexity (LOC, services, manual steps).',
          'Show you validated with users before declaring victory.',
          'Mention maintenance cost of the new solution — not just launch hype.',
        ],
      },
    ],
    references: [
      { label: 'Amazon LP — Invent and Simplify', url: 'https://www.amazon.jobs/content/en/our-workplace/leadership-principles' },
    ],
  },

  'bias-for-action': {
    title: 'Bias for Action',
    subtitle: 'Show calculated speed — you move when waiting costs more than reversible risk.',
    readingMinutes: 7,
    blocks: [
      {
        type: 'p',
        text: 'Bias for Action is not recklessness. Strong stories include a time constraint, a reversible decision, and monitoring after ship.',
      },
      {
        type: 'table',
        headers: ['STAR', 'Feature flag launch during competitor launch week'],
        rows: [
          ['Situation', 'Market window; full test suite would slip launch 2 weeks.'],
          ['Task', 'Tech lead for checkout experiment.'],
          ['Action', 'Shipped behind flag to 5% users, defined rollback triggers, staffed war room for 48h.'],
          ['Result', 'Experiment validated +3% conversion; full rollout in 10 days; no Sev-1.'],
        ],
      },
      {
        type: 'callout',
        tone: 'warn',
        text: 'If your story ended in outage with no safeguards, it reads as poor judgment — not bias for action.',
      },
      {
        type: 'h',
        text: 'Reversible vs one-way doors',
      },
      {
        type: 'p',
        text: 'Amazon distinguishes one-way door decisions (hard to undo) from two-way doors (reversible). Bias for Action applies most to two-way doors — ship, learn, adjust.',
      },
      {
        type: 'ul',
        items: [
          'Define rollback plan before launch.',
          'Use feature flags for gradual exposure.',
          'Set explicit time-box for experiment evaluation.',
        ],
      },
      {
        type: 'callout',
        tone: 'tip',
        title: 'Contrast with recklessness',
        text: '“We skipped all testing” fails. “We shipped to 1% with automated rollback triggers” passes.',
      },
      {
        type: 'callout',
        tone: 'interview',
        text: 'If asked about a time you moved too fast, show you updated process afterward — growth plus judgment.',
      },
    ],
    references: [
      { label: 'Amazon LP — Bias for Action', url: 'https://www.amazon.jobs/content/en/our-workplace/leadership-principles' },
    ],
  },

  'dive-deep': {
    title: 'Dive Deep',
    subtitle: 'Prove you go to first principles and data — not opinions from the loudest person in the room.',
    readingMinutes: 8,
    blocks: [
      {
        type: 'p',
        text: 'Dive Deep stories show you crossed layer boundaries: app → DB → network → vendor API. Include the “aha” moment from evidence.',
      },
      {
        type: 'steps',
        items: [
          { title: 'Symptom', text: 'What looked wrong at the surface?' },
          { title: 'Hypotheses', text: 'List what you ruled in/out and how.' },
          { title: 'Deep evidence', text: 'Query plans, packet captures, profiler, source code.' },
          { title: 'Fix at root', text: 'Not just restart — why restart worked temporarily.' },
        ],
      },
      {
        type: 'callout',
        tone: 'interview',
        title: 'Follow-up',
        text: '“Walk me through your debugging steps.” Practice a chronological list without jargon overload.',
      },
      {
        type: 'table',
        headers: ['STAR', 'Memory leak in production'],
        rows: [
          ['Situation', 'Service OOM restarted every 6 hours after deploy.'],
          ['Task', 'I owned JVM heap investigation.'],
          ['Action', 'Heap dump analysis, MAT dominator tree, found unbounded cache keyed by session without TTL.'],
          ['Result', 'Hotfix TTL + max size; stable 30 days; added heap growth alert.'],
        ],
      },
      {
        type: 'ul',
        items: [
          'Show you read primary sources — logs, metrics, code — not only second-hand reports.',
          'Admit wrong hypotheses you ruled out — shows scientific thinking.',
          'Connect fix to monitoring so regression is caught early.',
        ],
      },
      {
        type: 'callout',
        tone: 'tip',
        text: 'Bring one technical depth story even to behavioral rounds — staff+ loops expect it.',
      },
      {
        type: 'callout',
        tone: 'interview',
        text: 'Practice explaining technical root cause in 30 seconds, then business impact in 15 — interviewers may not be deep in your stack.',
      },
      {
        type: 'callout',
        tone: 'warn',
        text: 'Do not fake depth — if you only watched others debug, say your role was coordination; honesty beats getting caught in follow-ups.',
      },
    ],
    references: [
      { label: 'Amazon LP — Dive Deep', url: 'https://www.amazon.jobs/content/en/our-workplace/leadership-principles' },
    ],
  },

  'disagree-and-commit': {
    title: 'Disagree and Commit',
    subtitle: 'Disagree with substance, then execute the team decision as if it were yours.',
    readingMinutes: 8,
    blocks: [
      {
        type: 'p',
        text: 'This LP tests maturity: you voice dissent with data, escalate appropriately, and once decided, you do not sabotage or slow-walk.',
      },
      {
        type: 'table',
        headers: ['STAR', 'Tech stack decision'],
        rows: [
          ['Situation', 'Org chose vendor-managed Kafka over self-hosted despite my cost concerns.'],
          ['Task', 'I owned the migration workstream after the decision.'],
          ['Action', 'Documented risks in writing pre-decision; after commit, built migration runbook, trained team, hit cutover date.'],
          ['Result', 'Zero message loss at cutover; later data validated managed TCO was lower at our scale.'],
        ],
      },
      {
        type: 'callout',
        tone: 'tip',
        text: 'Show respect for the decision-maker. Commitment means full effort — interviewers listen for lingering bitterness.',
      },
      {
        type: 'h',
        text: 'Phrases that signal maturity',
      },
      {
        type: 'ul',
        items: [
          '“I disagreed and documented my concerns, then executed the plan fully.”',
          '“Once we decided, I stopped relitigating in hallway conversations.”',
          '“Six months later, data showed which approach was right — I updated my model.”',
        ],
      },
      {
        type: 'callout',
        tone: 'warn',
        title: 'Red flag',
        text: 'Stories where you undermined the decision after the meeting — passive resistance fails this LP hard.',
      },
      {
        type: 'callout',
        tone: 'interview',
        text: 'Pair with Have Backbone: disagreement must be substantive and data-backed, not performative.',
      },
      {
        type: 'callout',
        tone: 'tip',
        text: 'Good follow-up story: later data proved you wrong — and you acknowledged it publicly. Shows intellectual honesty.',
      },
    ],
    references: [
      { label: 'Amazon LP — Have Backbone; Disagree and Commit', url: 'https://www.amazon.jobs/content/en/our-workplace/leadership-principles' },
    ],
  },

  'deliver-results': {
    title: 'Deliver Results',
    subtitle: 'Close with outcomes — on time, on quality, with clarity on what you sacrificed to get there.',
    readingMinutes: 7,
    blocks: [
      {
        type: 'p',
        text: 'Deliver Results is the LP that ties impact to execution. Numbers, dates, and scope boundaries matter.',
      },
      {
        type: 'ul',
        items: [
          'Committed goal vs actual outcome.',
          'Obstacles removed (dependencies, tech debt, staffing).',
          'Quality bar: incidents, bugs, SLA.',
          'If you missed — what you shipped instead and how you communicated.',
        ],
      },
      {
        type: 'table',
        headers: ['Metric type', 'Examples'],
        rows: [
          ['Business', 'Revenue enabled, cost saved, conversion lift'],
          ['Reliability', 'SLA, error budget, MTTR'],
          ['Velocity', 'Cycle time, lead time, deployment frequency'],
          ['People', 'Team retention, onboarding time (if you led)'],
        ],
      },
      {
        type: 'callout',
        tone: 'interview',
        text: 'End every behavioral answer with a Result sentence — even if the interviewer does not ask. It anchors your story.',
      },
      {
        type: 'h',
        text: 'STAR example — deadline pressure',
      },
      {
        type: 'table',
        headers: ['STAR', 'Black Friday readiness'],
        rows: [
          ['Situation', 'Checkout service untested at 5× normal peak; launch 3 weeks away.'],
          ['Task', 'I owned load testing and remediation.'],
          ['Action', 'Built traffic replay, found DB pool bottleneck, scaled pool + index, ran game day with ops.'],
          ['Result', 'Peak day 99.97% success vs 99.5% SLA; zero Sev-1; postmortem only minor tuning items.'],
        ],
      },
      {
        type: 'steps',
        items: [
          { title: 'Define done', text: 'Explicit acceptance criteria and date.' },
          { title: 'Track risks', text: 'Weekly RAG status to stakeholders.' },
          { title: 'Cut scope', text: 'Negotiate MVP when time is fixed.' },
          { title: 'Report outcome', text: 'Numbers, not adjectives.' },
        ],
      },
      {
        type: 'callout',
        tone: 'tip',
        text: 'Delivering results under constraint (time, budget, people) is more impressive than perfect conditions.',
      },
    ],
    references: [
      { label: 'Amazon LP — Deliver Results', url: 'https://www.amazon.jobs/content/en/our-workplace/leadership-principles' },
    ],
  },
};
